import { useCallback, useEffect } from 'react'
import { useParams } from 'react-router'

import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Button from '@/elements/Button'
import Loading from '@/elements/Loading'
import FormToggle from '@/elements/FormToggle'
import ExternalLink from '@/elements/ExternalLink'
import useBreakpoints from '@/hooks/useBreakpoints'
import useDeactivateUser from '@/hooks/useDeactivateUser'
import useOrganizationMemberForm from '@/hooks/forms/useOrganizationMemberForm'
import { changeOrganizationMemberEmail } from '@/api/organizationMember'
import toast from '@/utils/toast'
import {
  createMemberIndividualProfile,
  updateOrganizationMember,
  useGetMemberIndividualStatus,
  useGetOrganizationMember
} from '@/api/organizationMember'

import OrganizationMemberForm from '../Organization/OrganizationMembers/OrganizationMemberForm'

function MemberPersonalDetails() {
  const { id: orgMemberIdParam } = useParams<{ id: string }>()
  const orgMemberId = orgMemberIdParam || ''

  const { isTablet, isMobile } = useBreakpoints()

  const {
    data: memberResponse,
    isLoading: isLoadingMember,
    refetch: refetchMember
  } = useGetOrganizationMember(orgMemberId)
  const memberData = memberResponse?.data || null

  const { form, resetForm } = useOrganizationMemberForm(memberData)
  const { DeactivateUserModal } = useDeactivateUser({
    userType: 'member',
    userId: orgMemberId,
    refetch: refetchMember,
    redirect: true
  })

  const {
    data: memberIndividualStatusData,
    refetch: refetchMemberIndividualStatus
  } = useGetMemberIndividualStatus(orgMemberId)
  const hasIndividualProfile = Boolean(memberIndividualStatusData?.data?.organizationIndividual?.id)

  const {
    DeactivateUserModal: DeactivateIndividualModal,
    setShowDeactivateModal: setShowDeactivateIndividualModal
  } = useDeactivateUser({
    userType: 'individual',
    userId: memberIndividualStatusData?.data?.organizationIndividual?.id || '',
    refetch: refetchMemberIndividualStatus,
  })

  useEffect(() => {
    resetForm(memberData)
  }, [memberData, resetForm])

  const submitUpdateMember = useCallback(async () => {
    const isValid = await form.trigger()
    if (isValid && orgMemberId) {
      const dirtyFields = form.formState.dirtyFields
      const values = form.getValues()

      if (dirtyFields.email && memberData?.organizationMemberId?.id) {
        try {
          await changeOrganizationMemberEmail(memberData.organizationMemberId.id, { email: values.email })
        } catch (error) {
          const message = (error as { message?: string })?.message
          toast.error(message || 'Failed to update email')
          return
        }
      }

      const hasOtherDirtyFields = Object.keys(dirtyFields).some(k => k !== 'email')
      if (hasOtherDirtyFields) {
        const formData = new FormData()
        Object.entries({
          ...(dirtyFields.avatar && {
            avatarAction: values.avatar ? 'update' : 'delete',
            ...(values.avatar ? { avatar: values.avatar } : {})
          }),
          ...(dirtyFields.firstName && { firstName: values.firstName }),
          ...(dirtyFields.lastName && { lastName: values.lastName || '' }),
          ...(dirtyFields.city && { city: values.city || '' }),
          ...(dirtyFields.state && { state: values.state || '' }),
          ...(dirtyFields.role && { role: values.role })
        }).forEach(([key, value]) => formData.append(key, value))

        try {
          await updateOrganizationMember(orgMemberId, formData)
        } catch {
          toast.error('Failed to update member')
          return
        }
      }

      refetchMember()
      toast.success('Member updated successfully!')
    }
  }, [form, memberData?.organizationMemberId?.id, orgMemberId, refetchMember])

  const submitCreateIndividualProfile = useCallback(async () => {
    if (orgMemberId) {
      try {
        await createMemberIndividualProfile(orgMemberId)
        refetchMemberIndividualStatus()
        toast.success('Individual Monitoring Profile created successfully!')
      } catch {
        toast.error('Failed to create Individual Monitoring Profile')
      }
    }
  }, [orgMemberId, refetchMemberIndividualStatus])

  return (
    <Flex
      wfull
      className='card'
      align='flex-end'
      gap={8}
      p={isMobile ? 3 : isTablet ? 5 : 7}
    >
      {isLoadingMember ? (
        <Flex align='center' justify='center' wfull py={8}>
          <Loading />
        </Flex>
      ) : (
        <>
          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Personal details</Text>
              <Text color='black-light-40'>
                Update member's personal information and profile details.
              </Text>
            </Flex>
            <Flex wfull fraction={6} gap={4} align='end'>
              <OrganizationMemberForm
                form={form}
                mode='edit'
                onSubmit={submitUpdateMember}
              />
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline={!isTablet} gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Permissions</Text>
              <Text variant='h4' color='black-light-40'>
                Manage your role permissions inside the organization.
              </Text>
            </Flex>
            <Flex fraction={6} gap={!isMobile ? 5 : 3} wfull>
              <Flex
                wfull
                inline
                gap={2}
                align='center'
                justify='space-between'
              >
                <Flex wfull>
                  <Text variant='h4' color='black'>
                    Individual Monitoring Profile
                  </Text>
                  <Text variant='textblock' color='black-light-40'>
                    You will be monitored as an individual.
                  </Text>
                </Flex>
                <FormToggle
                  name='theme-switcher'
                  value={hasIndividualProfile}
                  onChange={hasIndividualProfile
                    ? () => setShowDeactivateIndividualModal(true)
                    : submitCreateIndividualProfile
                  }
                />
                <DeactivateIndividualModal />
              </Flex>
              {hasIndividualProfile && (
                <ExternalLink
                  to={`/individual/${memberIndividualStatusData?.data?.subject?.id}/details`}
                  variant='black'
                  size='small'
                >
                  Manage user as an individual
                </ExternalLink>
              )}
            </Flex>
          </Flex>

          <Flex color='gray-light-20' wfull style={{ height: '1px' }} />

          <Flex wfull inline align='center' gap={4} wrap>
            <Flex fraction={4} gap={2} wfull>
              <Text variant='h3'>Other actions</Text>
            </Flex>
            <Flex fraction={6} wfull align='end' hfull>
              <DeactivateUserModal>
                <Button
                  variant='danger'
                  styles={{ border: 0, whiteSpace: 'nowrap' }}
                >
                  Deactivate User
                </Button>
              </DeactivateUserModal>
            </Flex>
          </Flex>
        </>
      )}
    </Flex>
  )
}

export default MemberPersonalDetails
